import { useEffect, useMemo, useRef } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import gsap from 'gsap';
import * as THREE from 'three';
import { useGameStore } from '../../core/store';
import { useIsMobile } from '../../core/useIsMobile';
import { play, stopIntroBed, startTunnelBed } from '../audio';
/**
 * Timeline:
 * 0–2s wide
 * 2–12.5s slow push into locker
 * 12.5–14.5s hold on jersey
 * 14.5s identity UI
 * claim → black → (closing) door sound → tunnel
 */
export function LockerCamera() {
  const { camera, size } = useThree();
  const mobile = useIsMobile();
  const progress = useRef({ t: 0 });
  const beat = useGameStore((s) => s.lockerBeat);
  const setOverlayOpacity = useGameStore((s) => s.setOverlayOpacity);
  const showLockerIdentity = useGameStore((s) => s.showLockerIdentity);
  const enterTunnelFromLocker = useGameStore((s) => s.enterTunnelFromLocker);
  const identityShown = useRef(false);
  const closingRan = useRef(false);

  useEffect(() => {
    return () => {
      if (camera instanceof THREE.PerspectiveCamera) {
        camera.clearViewOffset();
      }
    };
  }, [camera]);

  const curve = useMemo(() => {
    if (mobile) {
      const end = new THREE.Vector3(0, 1.5, -1.9);
      return new THREE.CatmullRomCurve3(
        [
          // Same slow "arrive from the dark" distance as desktop — the old
          // 5.8 start made the lit jersey read as already-close/immediate.
          new THREE.Vector3(0, 1.5, 7.0),
          new THREE.Vector3(0, 1.5, 4.2),
          new THREE.Vector3(0, 1.5, 1.6),
          new THREE.Vector3(0, 1.5, -0.8),
          end.clone(),
          end.clone(),
          new THREE.Vector3(0.05, 1.5, -1.5),
          new THREE.Vector3(0.35, 1.52, -2.2),
          new THREE.Vector3(0.12, 1.55, 1.2),
          new THREE.Vector3(0, 1.55, 5.2),
        ],
        false,
        'catmullrom',
        0.25,
      );
    }

    const close = new THREE.Vector3(0.14, 1.72, -2.72);
    const hold = close.clone();
    return new THREE.CatmullRomCurve3(
      [
        new THREE.Vector3(0.08, 1.75, 7.2),
        new THREE.Vector3(0.1, 1.72, 4.0),
        new THREE.Vector3(0.12, 1.7, 0.35),
        new THREE.Vector3(0.13, 1.7, -2.0),
        close,
        hold,
        new THREE.Vector3(0.05, 1.55, -1.8),
        new THREE.Vector3(0.4, 1.55, -2.5),
        new THREE.Vector3(0.15, 1.55, 1.2),
        new THREE.Vector3(0, 1.55, 5.2),
      ],
      false,
      'catmullrom',
      0.35,
    );
  }, [mobile]);

  const lookCurve = useMemo(() => {
    if (mobile) {
      const end = new THREE.Vector3(0, 1.55, -4.72);
      return new THREE.CatmullRomCurve3(
        [
          new THREE.Vector3(0, 1.5, -4.5),
          new THREE.Vector3(0, 1.52, -4.6),
          new THREE.Vector3(0, 1.54, -4.68),
          new THREE.Vector3(0, 1.55, -4.7),
          end.clone(),
          end.clone(),
          new THREE.Vector3(0, 1.55, -4.55),
          new THREE.Vector3(0, 1.45, 6),
          new THREE.Vector3(0, 1.5, 7),
          new THREE.Vector3(0, 1.55, 8),
        ],
        false,
        'catmullrom',
        0.25,
      );
    }

    const lookX = 0;
    const jerseyY = 1.52;
    return new THREE.CatmullRomCurve3(
      [
        new THREE.Vector3(lookX, 1.35, -4.5),
        new THREE.Vector3(lookX, 1.4, -4.65),
        new THREE.Vector3(lookX, 1.45, -4.7),
        new THREE.Vector3(lookX, 1.5, -4.72),
        new THREE.Vector3(lookX, jerseyY, -4.7),
        new THREE.Vector3(lookX, 1.5, -4.55),
        new THREE.Vector3(0, 1.4, -4.6),
        new THREE.Vector3(0, 1.45, 6),
        new THREE.Vector3(0, 1.5, 7),
        new THREE.Vector3(0, 1.55, 8),
      ],
      false,
      'catmullrom',
      0.35,
    );
  }, [mobile]);

  useEffect(() => {
    const proxy = progress.current;
    proxy.t = 0;
    identityShown.current = false;
    closingRan.current = false;

    setOverlayOpacity(0);

    const endT = mobile ? 0.45 : 0.44;

    const tl = gsap.timeline();
    tl.to(proxy, { t: 0.04, duration: 1.6, ease: 'none' }, 0);
    tl.to(proxy, { t: endT, duration: 11.2, ease: 'power2.inOut' }, 1.6);
    tl.to(proxy, { t: endT, duration: 1.8, ease: 'none' }, 12.8);
    tl.add(() => {
      if (!identityShown.current) {
        identityShown.current = true;
        showLockerIdentity();
      }
    }, 14.5);

    const failsafe = window.setTimeout(() => {
      setOverlayOpacity(0);
      if (!identityShown.current) {
        identityShown.current = true;
        showLockerIdentity();
      }
    }, 16000);

    return () => {
      tl.kill();
      window.clearTimeout(failsafe);
    };
  }, [setOverlayOpacity, showLockerIdentity, mobile]);

  /**
   * After claim black: fade locker music → door slam → tunnel bed → handoff.
   * Overlay stays black; iris reveal continues in App.
   */
  useEffect(() => {
    if (beat !== 'closing' || closingRan.current) return;
    closingRan.current = true;

    setOverlayOpacity(1);
    const timers: number[] = [];

    // Soft fade of dressing-room bed — never hard-cut
    stopIntroBed(2.6);

    timers.push(
      window.setTimeout(() => {
        void play('door', { gain: 1.15, rate: 0.96 });
        void play('silence', { gain: 0.35 });
      }, 920),
    );

    timers.push(
      window.setTimeout(() => {
        void startTunnelBed();
      }, 1500),
    );

    // Hand off after music/door. Do NOT startTunnelWarming() while locker
    // Canvas is still alive — dual WebGL contexts caused bare Context Lost
    // (GameCanvas stayed healthy, so window.__webglLost stayed false).
    timers.push(
      window.setTimeout(() => {
        enterTunnelFromLocker();
      }, 2100),
    );

    return () => {
      timers.forEach((id) => window.clearTimeout(id));
    };
  }, [beat, enterTunnelFromLocker, setOverlayOpacity]);

  useFrame(() => {
    const endT = mobile ? 0.45 : 0.44;
    if (beat === 'claim' || beat === 'closing') {
      progress.current.t = endT;
    }
    const t = THREE.MathUtils.clamp(progress.current.t, 0, 1);
    const pos = curve.getPoint(t);
    const look = lookCurve.getPoint(t);
    // Same slow follow speed as desktop — mobile used to snap in noticeably
    // faster (0.32), which read as an instant cut instead of a build-up.
    const mix = t < 0.03 ? 1 : 0.2;
    camera.position.lerp(pos, mix);
    camera.lookAt(look);
    if (camera instanceof THREE.PerspectiveCamera) {
      const tight = mobile ? 50 : 42;
      const wide = mobile ? 48 : 46;
      const u = THREE.MathUtils.smoothstep(t / endT, 0, 1);
      camera.fov = THREE.MathUtils.lerp(wide, tight, u);
      if (mobile) {
        // Keep name + number in frame above the identity sheet
        const compose = 0.1;
        camera.setViewOffset(
          size.width,
          size.height,
          0,
          compose * size.height,
          size.width,
          size.height,
        );
      } else if (camera.view) {
        camera.clearViewOffset();
      }
      camera.updateProjectionMatrix();
    }
  });

  return null;
}
